import { LitElement, html, svg, css, nothing } from 'lit';
import { PRINT_STYLES } from './print-styles.js';
import { repeat } from 'lit/directives/repeat.js';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { StoreMixin, store } from '../store.js';
import { api } from '../api.js';
import { navigate } from '../router.js';
import { apiParams } from '../lib/filters.js';
import { CONFIDENCE_TIERS } from '../lib/palette.js';
import { fillFor, patternDefs } from '../lib/patterns.js';
import { withFormats } from '../lib/table.js';
import {
  BIPARTITE_MAX_PEOPLE,
  CONTENTION_KINDS,
  CONTENTION_ORDERS,
  CONTENTION_PARAM_KEYS,
  CONTENTION_SPANS,
  CONTENTION_VALUES,
  arcLabel,
  bipartiteLayout,
  contentionChordColumns,
  contentionChordRows,
  contentionContributorColumns,
  contentionContributorRows,
  contentionModel,
  contentionNodeColumns,
  contentionNodeRows,
  contentionPeopleColumns,
  contentionPeopleRows,
  contentionSummary,
  incident,
  ringLayout,
  suggestLayout,
  trimLabel,
} from '../lib/contention.js';
import { weekOptions, resolveWeek } from '../lib/matrix.js';
import { hours as formatHours, percent, longDate, count } from '../lib/format.js';

/** The ring's own coordinate space. Fixed, so the chart scales with the card and nothing jumps. */
const RING = Object.freeze({
  width: 760,
  height: 560,
  radius: 176,
  thickness: 13,
  /** How many characters a name is trimmed to beside an arc. The whole name is on the arc. */
  label: 22,
});

/** The bipartite's own space. Its height follows the rows, so only the width is fixed. */
const BIPARTITE = Object.freeze({
  width: 760,
  block: 11,
  gap: 5,
  rowHeight: 26,
  minHeight: 260,
  label: 26,
  /** The room the two columns of names need, inside the width. */
  gutter: 150,
});

/** How far a mark that is not in the isolation fades. Interaction state, never an encoding. */
const DIMMED = 0.16;

const KIND_LABELS = { project: 'Projects', recurring: 'Retainers', opportunity: 'Deals' };
const SPAN_LABELS = { window: 'Window', week: 'Week' };
const VALUE_LABELS = { hours: 'Shared hours', people: 'Shared people' };
const ORDER_LABELS = { shared: 'Shared', hours: 'Hours', name: 'Name', type: 'Type' };
const LAYOUT_LABELS = { chord: 'Chord', bipartite: 'Bipartite' };
/** The thresholds the "hide under" control offers, in hours of shared time. */
const FLOORS = [0, 1, 4, 8, 20];

const plural = (n, one, many = `${one}s`) => `${count(n)} ${n === 1 ? one : many}`;
const exact = (v) => formatHours(v, { exact: true });

/**
 * The contention map (Design Review V-9; TRD 10.2; Phase 44): which projects compete for the
 * same people.
 *
 * Every other view on this page is about a quantity — hours, a ratio, a band. This one is about
 * a *structure*. A person at 95 per cent across four projects is inside every threshold on the
 * dashboard and is the most fragile thing in the portfolio, because the moment one of the four
 * slips they are three problems and a fire. Nothing else here shows that coupling: the Gantt
 * shows the bars, the matrix shows the cells, and neither draws the line between two projects
 * that cannot both have the same designer in the same week.
 *
 * A chord's width is what the pair cannot share: per person and per week, `min(hours on A,
 * hours on B)`, summed. Taking the minimum **inside the week** is the load-bearing choice — two
 * projects in different months compete for nobody, and a minimum of two window totals would say
 * they did. It is also what makes the Week span reconcile cell for cell with the assignment
 * matrix above it, which is the gate's own check.
 *
 * The **bipartite alternate** is V-9's second suggestion and the card opens on it for a small
 * team: people down the left, projects down the right, a band per assignment as thick as its
 * hours. It draws the hours themselves rather than their minimum, so it answers "who is
 * stretched" where the ring answers "what is coupled".
 *
 * Every number comes from `GET /forecast/contention` and every position from
 * `lib/contention.js`, which the CSV export imports too. This component formats, places nothing
 * of its own, and computes no hours.
 */
class ContentionMap extends StoreMixin(LitElement) {
  static properties = {
    week: { type: String }, // the page's selected week, shared with the assignment matrix
    _data: { state: true },
    _loading: { state: true },
    _error: { state: true },
    _span: { state: true },
    _layout: { state: true }, // null follows the response's own size
    _value: { state: true },
    _order: { state: true },
    _kinds: { state: true },
    _floor: { state: true },
    _q: { state: true },
    _hover: { state: true },
    _pinned: { state: true },
    _tableOpen: { state: true },
  };

  constructor() {
    super();
    this.week = null;
    this._data = null;
    this._loading = false;
    this._error = null;
    this._span = 'window';
    this._layout = null;
    this._value = 'hours';
    this._order = 'shared';
    this._kinds = [...CONTENTION_KINDS];
    this._floor = 0;
    this._q = '';
    this._hover = null;
    this._pinned = null;
    this._tableOpen = false;
    this._requestKey = null;
  }

  static styles = [
    PRINT_STYLES,
    css`
      :host {
        display: block;
        min-width: 0;
      }
      .card {
        background: var(--surface);
        border: 1px solid var(--border-soft);
        border-radius: var(--radius-card);
        box-shadow: var(--shadow-card);
        padding: 20px 24px 18px;
        min-width: 0;
      }
      .head {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        gap: 6px 18px;
        margin-bottom: 12px;
      }
      .eyebrow {
        font-family: var(--font-eyebrow);
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.16em;
        text-transform: uppercase;
        color: var(--ink-muted);
      }
      h2 {
        margin: 2px 0 0;
        font-size: 20px;
        line-height: 1.2;
        color: var(--ink-strong);
      }
      .sub {
        color: var(--ink-muted);
        font-size: 12.5px;
        max-width: 72ch;
      }
      .kpis {
        margin-left: auto;
        display: flex;
        gap: 18px;
        flex-wrap: wrap;
      }
      .kpi {
        text-align: right;
      }
      .kpi .n {
        font-size: 18px;
        font-weight: 700;
        color: var(--ink-strong);
        font-variant-numeric: tabular-nums;
        line-height: 1.1;
      }
      .kpi .l {
        font-size: 11px;
        color: var(--ink-muted);
      }
      .controls {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px 16px;
        margin-bottom: 8px;
        font-size: 12.5px;
        color: var(--ink-muted);
      }
      .segmented {
        display: inline-flex;
        align-items: center;
        gap: 6px;
      }
      .segmented .group {
        display: inline-flex;
        border: 1px solid var(--control-border);
        border-radius: var(--radius-pill);
        background: var(--control-bg);
        padding: 2px;
      }
      .segmented .group button {
        font: inherit;
        border: 0;
        background: none;
        border-radius: var(--radius-pill);
        padding: 3px 10px;
        font-size: 12.5px;
        font-weight: 600;
        color: var(--ink-muted);
        cursor: pointer;
      }
      .segmented .group button[aria-pressed='true'] {
        color: var(--ink-on-brand);
        background: var(--ink-strong);
      }
      label.field {
        display: inline-flex;
        align-items: center;
        gap: 6px;
      }
      select,
      input[type='search'] {
        font: inherit;
        font-size: 12.5px;
        border: 1px solid var(--control-border);
        border-radius: var(--radius-pill);
        background: var(--control-bg);
        color: var(--ink);
        padding: 3px 10px;
      }
      button.link {
        font: inherit;
        font-size: 12.5px;
        border: 0;
        background: none;
        color: var(--link);
        padding: 0;
        font-weight: 600;
        cursor: pointer;
      }

      /* ---- the marks ---- */
      .plot {
        width: 100%;
        height: auto;
        display: block;
        overflow: visible;
        touch-action: manipulation;
      }
      .arc,
      .ribbon,
      .band,
      .block {
        cursor: pointer;
      }
      .arc {
        stroke-width: 1;
      }
      .ribbon {
        fill-opacity: 0.5;
        stroke-width: 0.5;
        stroke-opacity: 0.45;
      }
      .band {
        fill-opacity: 0.42;
        stroke-width: 0.5;
        stroke-opacity: 0.4;
      }
      g[data-dim='true'] {
        opacity: ${DIMMED};
      }
      g[data-lit='true'] .ribbon,
      g[data-lit='true'] .band {
        fill-opacity: 0.72;
      }
      .mark:focus-visible {
        outline: 2px solid var(--focus);
      }
      .label {
        font-size: 11px;
        font-family: var(--font-sans);
        fill: var(--tick-ink);
      }
      .label.pinned {
        fill: var(--ink-strong);
        font-weight: 700;
      }
      .axis {
        font-size: 10.5px;
        font-family: var(--font-sans);
        fill: var(--ink-muted);
        letter-spacing: 0.06em;
        text-transform: uppercase;
      }
      .middle {
        font-family: var(--font-sans);
        fill: var(--ink-muted);
        font-size: 12px;
      }
      .middle .n {
        font-size: 24px;
        font-weight: 700;
        fill: var(--ink-strong);
      }

      /* ---- chrome ---- */
      .legends {
        display: grid;
        gap: 8px;
        margin-top: 12px;
      }
      .legend {
        display: flex;
        flex-wrap: wrap;
        gap: 6px 16px;
        padding: 0;
        margin: 0;
        list-style: none;
        font-size: 12px;
        color: var(--ink-muted);
      }
      .legend li {
        display: inline-flex;
        align-items: center;
        gap: 6px;
      }
      .legend .sw {
        width: 22px;
        height: 12px;
        border-radius: 2px;
        border: 1px solid var(--border-soft);
        flex: none;
      }
      .note {
        margin: 10px 0 0;
        font-size: 12px;
        color: var(--ink-muted);
        max-width: 88ch;
      }
      .state {
        padding: 26px 0;
        color: var(--ink-muted);
        font-size: 13px;
      }
      .state.error {
        color: var(--status-overloaded);
      }
      details.table {
        margin-top: 14px;
      }
      details.table summary {
        cursor: pointer;
        font-size: 12.5px;
        color: var(--ink-muted);
      }
      details.table data-table {
        display: block;
        margin-top: 10px;
      }
      @media (max-width: 720px) {
        .card {
          padding: 16px 14px 14px;
        }
      }
    `,
  ];

  // -------------------------------------------------------------------------------------------
  // Data

  connectedCallback() {
    super.connectedCallback();
    this._unsubscribeFetch = store.subscribe(() => this._maybeFetch());
    this._maybeFetch();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._unsubscribeFetch?.();
  }

  /** The weeks the shared window offers, clipped to the run's horizon — the matrix's own list. */
  get _weeks() {
    const { filters, run } = this.appState;
    return weekOptions(filters, run);
  }

  get _resolvedWeek() {
    return resolveWeek(this._weeks, this.week);
  }

  /**
   * The shared filters this view reads, plus the span. The ordering, the width rule, the kinds
   * and the search stay in the browser: they hide marks, and a node's own contention figure must
   * not follow what is hidden.
   */
  _params() {
    const { run } = this.appState;
    if (!run) return null;
    const params = apiParams(this.appState.filters, CONTENTION_PARAM_KEYS);
    params.span = this._span;
    if (this._span === 'week') {
      const week = this._resolvedWeek;
      if (!week) return null;
      params.week = week;
    }
    return params;
  }

  async _maybeFetch() {
    const params = this._params();
    const key = params ? JSON.stringify(params) : null;
    if (key === this._requestKey) return;
    this._requestKey = key;
    if (!params) {
      this._data = null;
      return;
    }
    this._loading = true;
    this._error = null;
    try {
      const data = await api.get('/forecast/contention', params);
      if (this._requestKey !== key) return; // a newer request has taken over
      this._data = data;
    } catch (err) {
      if (this._requestKey !== key) return;
      this._error = err.message;
      this._data = null;
    } finally {
      if (this._requestKey === key) this._loading = false;
    }
  }

  /** The page's week reaches the card as a property; the store does not change when it does. */
  updated(changed) {
    if (changed.has('week') && this._span === 'week') queueMicrotask(() => this._maybeFetch());
  }

  // -------------------------------------------------------------------------------------------
  // Derived

  get _model() {
    if (!this._data) return null;
    const { palette } = this.appState;
    const key = `${this._value}|${this._order}|${this._kinds.join(',')}|${this._floor}|${this._q}`;
    if (this._modelFor !== this._data || this._modelKey !== key) {
      this._model_ = contentionModel(this._data, {
        value: this._value,
        order: this._order,
        kinds: this._kinds,
        minShared: this._floor,
        q: this._q,
        palette: palette ?? [],
      });
      this._modelFor = this._data;
      this._modelKey = key;
    }
    return this._model_;
  }

  /** The layout drawn: the reader's choice, else the one the response's size asks for. */
  _layoutOf(model) {
    return this._layout ?? suggestLayout(model);
  }

  /** What is lit: what the cursor is on, else what the reader pinned. */
  get _focus() {
    return this._hover ?? this._pinned;
  }

  // -------------------------------------------------------------------------------------------
  // Interaction

  _setSpan(span) {
    if (this._span === span) return;
    this._span = span;
    // Choosing the week span with no week chosen lands on the matrix's own week, so the two
    // cards on the page are the same week without the reader doing anything.
    if (span === 'week' && !this.week) {
      const week = this._resolvedWeek;
      if (week) this._announceWeek(week);
    }
    this._maybeFetch();
  }

  _announceWeek(weekStart) {
    this.dispatchEvent(
      new CustomEvent('week-change', {
        detail: { week_start: weekStart },
        bubbles: true,
        composed: true,
      }),
    );
  }

  _toggleKind(kind) {
    const has = this._kinds.includes(kind);
    const next = has ? this._kinds.filter((k) => k !== kind) : [...this._kinds, kind];
    this._kinds = next.length ? next : [...CONTENTION_KINDS];
  }

  _pin(key) {
    this._pinned = this._pinned === key ? null : key;
  }

  _openNode(node) {
    if (node.kind === 'opportunity' || !node.id) return;
    navigate(`/projects/${node.id}`);
  }

  _show(event, title, rows, footer) {
    const tooltip = this.renderRoot.querySelector('chart-tooltip');
    if (!tooltip) return;
    tooltip.show({
      x: event.clientX ?? 0,
      y: event.clientY ?? 0,
      title,
      rows: rows.filter(Boolean),
      footer: footer || '',
    });
  }

  _hide() {
    this.renderRoot.querySelector('chart-tooltip')?.hide();
  }

  _showNode(event, model, node) {
    this._show(
      event,
      node.name,
      [
        node.client ? { label: 'Client', value: node.client } : null,
        { label: 'Hours in the span', value: exact(node.hours) },
        { label: '  On named people', value: exact(node.people_hours) },
        node.unassigned_hours > 0
          ? { label: '  Role-only, nobody named', value: exact(node.unassigned_hours) }
          : null,
        { label: 'People', value: count(node.people) },
        { label: 'Shared with others', value: exact(node.shared_hours) },
        { label: 'Competing projects', value: count(node.competitors) },
        node.withheld_people > 0
          ? { label: 'People you may not see', value: count(node.withheld_people) }
          : null,
      ],
      [
        node.kind === 'opportunity'
          ? `Deal${node.stage_name ? ` · ${node.stage_name}` : ''}`
          : null,
        node.kind === 'recurring' ? 'Retainer' : null,
        node.canonical_type,
        node.confidence_tier ? `${node.confidence_tier} confidence` : null,
        node.kind === 'opportunity' ? null : 'click to open the project',
      ]
        .filter(Boolean)
        .join(' · '),
    );
  }

  _showChord(event, model, chord) {
    const names = (chord.contributors ?? [])
      .slice(0, 4)
      .map((p) => `${p.label} ${exact(p.shared_hours)}`);
    const more = Math.max(0, (chord.contributors ?? []).length - names.length);
    this._show(
      event,
      `${chord.a_name} ↔ ${chord.b_name}`,
      [
        { label: 'Shared', value: exact(chord.shared_hours) },
        { label: '  Biggest single overlap', value: exact(chord.peak_hours) },
        { label: 'Shared people', value: count(chord.people) },
        { label: 'Weeks they overlap', value: count(chord.weeks) },
        { label: `Their hours on ${chord.a_name}`, value: exact(chord.a_hours) },
        { label: `Their hours on ${chord.b_name}`, value: exact(chord.b_hours) },
        chord.withheld_people > 0
          ? {
              label: 'Also shared, not named',
              value: `${exact(chord.withheld_hours)} · ${plural(chord.withheld_people, 'person', 'people')}`,
            }
          : null,
        ...names.map((n, i) => ({ label: i === 0 ? 'Who' : ' ', value: n })),
        more > 0 ? { label: ' ', value: `and ${count(more)} more — see the table` } : null,
      ],
      chord.confidence_tier ? `${chord.confidence_tier} confidence — the shakier of the two` : '',
    );
  }

  _showPerson(event, person) {
    this._show(
      event,
      person.label,
      [
        person.entity?.department ? { label: 'Department', value: person.entity.department } : null,
        { label: 'Hours in the span', value: exact(person.hours) },
        { label: 'Projects and deals', value: count(person.drawn_entities ?? person.entities) },
        { label: 'Shared with others', value: exact(person.shared_hours) },
        { label: 'Pairs they couple', value: count(person.competitors) },
      ],
      person.competitors > 0
        ? 'every pair on the ring this person is the reason for'
        : 'on one thing only — nobody is competing for them',
    );
  }

  // -------------------------------------------------------------------------------------------
  // The ring

  _ring(model) {
    const cx = RING.width / 2;
    const cy = RING.height / 2;
    const layout = ringLayout(model, {
      cx,
      cy,
      radius: RING.radius,
      thickness: RING.thickness,
    });
    const lit = incident(model, this._focus);
    const summary = contentionSummary(model);
    const byKey = new Map(model.nodes.map((n) => [n.key, n]));

    const dimRibbon = (key) => Boolean(lit) && !lit.chords.has(key);
    const dimArc = (key) => Boolean(lit) && !lit.nodes.has(key);

    return svg`
      <svg
        class="plot"
        viewBox="0 0 ${RING.width} ${RING.height}"
        role="img"
        aria-label=${`${plural(layout.arcs.length, 'project')} sharing people, ${plural(
          layout.ribbons.length,
          'pair',
        )} between them`}
        @pointerleave=${() => {
          this._hover = null;
          this._hide();
        }}
      >
        ${unsafeSVG(patternDefs())}
        <g>
          ${repeat(
            layout.ribbons,
            (r) => r.key,
            (r) => {
              const source = byKey.get(r.source);
              const slot = source?.slot?.var ?? '--type-other';
              return svg`<g
                class="ribbon-g"
                data-key=${r.key}
                data-dim=${dimRibbon(r.key)}
                data-lit=${Boolean(lit) && lit.chords.has(r.key)}
              >
                <path
                  class="ribbon mark"
                  role="button"
                  tabindex="0"
                  d=${r.path}
                  fill=${fillFor(slot, r.chord.fill)}
                  stroke=${`var(${slot})`}
                  @pointerenter=${(e) => {
                    this._hover = r.key;
                    this._showChord(e, model, r.chord);
                  }}
                  @pointermove=${(e) => this._showChord(e, model, r.chord)}
                  @focus=${() => (this._hover = r.key)}
                  @blur=${() => (this._hover = null)}
                  @click=${() => this._pin(r.key)}
                  @keydown=${(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      this._pin(r.key);
                    }
                  }}
                >
                  <title>
                    ${r.chord.a_name} and ${r.chord.b_name} share ${exact(r.chord.shared_hours)}
                    across ${plural(r.chord.people, 'person', 'people')}
                  </title>
                </path>
              </g>`;
            },
          )}
        </g>
        <g>
          ${repeat(
            layout.arcs,
            (a) => a.key,
            (a) => {
              const node = a.node;
              const slot = node.slot?.var ?? '--type-other';
              const label = arcLabel(a, {
                cx,
                cy,
                radius: RING.radius + RING.thickness,
                offset: 7,
              });
              return svg`<g
                class="arc-g"
                data-key=${a.key}
                data-kind=${node.kind}
                data-dim=${dimArc(a.key)}
              >
                <path
                  class="arc mark"
                  role="button"
                  tabindex="0"
                  d=${a.path}
                  fill=${fillFor(slot, node.fill)}
                  stroke=${`var(${slot})`}
                  @pointerenter=${(e) => {
                    this._hover = a.key;
                    this._showNode(e, model, node);
                  }}
                  @pointermove=${(e) => this._showNode(e, model, node)}
                  @focus=${() => (this._hover = a.key)}
                  @blur=${() => (this._hover = null)}
                  @click=${(e) => (e.altKey ? this._openNode(node) : this._pin(a.key))}
                  @dblclick=${() => this._openNode(node)}
                  @keydown=${(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      this._openNode(node);
                    }
                    if (e.key === ' ') {
                      e.preventDefault();
                      this._pin(a.key);
                    }
                  }}
                >
                  <title>
                    ${node.name} — ${exact(node.shared_hours)} shared with
                    ${plural(node.competitors, 'project')}
                  </title>
                </path>
                <text
                  class=${`label${this._pinned === a.key ? ' pinned' : ''}`}
                  x=${label.x}
                  y=${label.y}
                  text-anchor=${label.anchor}
                  dominant-baseline="middle"
                  transform=${`rotate(${label.rotate} ${label.x} ${label.y})`}
                >${trimLabel(node.name, RING.label)}</text>
              </g>`;
            },
          )}
        </g>
        <g class="middle" text-anchor="middle" pointer-events="none">
          <text class="n" x=${cx} y=${cy - 2}>${exact(summary.shared_hours)}</text>
          <text x=${cx} y=${cy + 16}>contended across ${plural(summary.chords, 'pair')}</text>
        </g>
      </svg>
    `;
  }

  // -------------------------------------------------------------------------------------------
  // The bipartite alternate

  _bipartite(model) {
    const rows = Math.max(model.people.length, model.nodes.length);
    const height = Math.max(BIPARTITE.minHeight, rows * BIPARTITE.rowHeight);
    const inner = BIPARTITE.width - BIPARTITE.gutter * 2;
    const layout = bipartiteLayout(model, {
      width: inner,
      height,
      gap: BIPARTITE.gap,
      blockWidth: BIPARTITE.block,
      top: 18,
    });
    const lit = incident(model, this._focus);
    const byKey = new Map(model.nodes.map((n) => [n.key, n]));
    const peopleBy = new Map(model.people.map((p) => [p.key, p]));
    const litBand = (band) =>
      !lit ||
      lit.nodes.has(band.edge.entity) ||
      this._focus === band.edge.person ||
      this._focus === band.key;

    const total = layout.height + 30;
    return svg`
      <svg
        class="plot"
        viewBox="0 0 ${BIPARTITE.width} ${total}"
        role="img"
        aria-label=${`${plural(layout.left.length, 'person', 'people')} across ${plural(
          layout.right.length,
          'project',
        )}`}
        @pointerleave=${() => {
          this._hover = null;
          this._hide();
        }}
      >
        ${unsafeSVG(patternDefs())}
        <g class="axis">
          <text x="0" y="10">People</text>
          <text x=${BIPARTITE.width} y="10" text-anchor="end">Projects and deals</text>
        </g>
        <g transform=${`translate(${BIPARTITE.gutter} 0)`}>
          ${repeat(
            layout.bands,
            (b) => b.key,
            (b) => {
              const node = byKey.get(b.edge.entity);
              const slot = node?.slot?.var ?? '--type-other';
              const person = peopleBy.get(b.edge.person);
              return svg`<g
                class="band-g"
                data-key=${b.key}
                data-dim=${!litBand(b)}
                data-lit=${Boolean(lit) && litBand(b)}
              >
                <path
                  class="band mark"
                  role="button"
                  tabindex="0"
                  d=${b.path}
                  fill=${fillFor(slot, node?.fill ?? 'solid')}
                  stroke=${`var(${slot})`}
                  @pointerenter=${(e) => {
                    this._hover = b.key;
                    this._show(
                      e,
                      `${person?.label ?? 'Someone'} on ${node?.name ?? 'a project'}`,
                      [
                        { label: 'Hours in the span', value: exact(b.edge.hours) },
                        { label: 'Worst single week', value: exact(b.edge.peak_hours) },
                        { label: 'Weeks', value: count(b.edge.weeks) },
                      ],
                      'the hours themselves — the ring draws what a pair cannot share',
                    );
                  }}
                  @focus=${() => (this._hover = b.key)}
                  @blur=${() => (this._hover = null)}
                  @click=${() => this._pin(b.key)}
                >
                  <title>
                    ${person?.label} on ${node?.name}: ${exact(b.edge.hours)}
                  </title>
                </path>
              </g>`;
            },
          )}
          ${repeat(
            layout.left,
            (b) => b.key,
            (b) => svg`<g
              class="person-g"
              data-key=${b.key}
              data-dim=${Boolean(lit) && this._focus !== b.key && !lit.nodes.has(b.key)}
            >
              <rect
                class="block mark"
                role="button"
                tabindex="0"
                x="0"
                y=${b.top}
                width=${BIPARTITE.block}
                height=${b.height}
                rx="2"
                fill="var(--ink-strong)"
                @pointerenter=${(e) => {
                  this._hover = b.key;
                  this._showPerson(e, b.row);
                }}
                @focus=${() => (this._hover = b.key)}
                @blur=${() => (this._hover = null)}
                @click=${() => this._pin(b.key)}
              ></rect>
              <text
                class="label"
                x="-8"
                y=${b.top + b.height / 2}
                text-anchor="end"
                dominant-baseline="middle"
              >${trimLabel(b.row.label, BIPARTITE.label)}</text>
            </g>`,
          )}
          ${repeat(
            layout.right,
            (b) => b.key,
            (b) => {
              const slot = b.row.slot?.var ?? '--type-other';
              return svg`<g
                class="node-g"
                data-key=${b.key}
                data-dim=${Boolean(lit) && !lit.nodes.has(b.key)}
              >
                <rect
                  class="block mark"
                  role="button"
                  tabindex="0"
                  x=${b.x}
                  y=${b.top}
                  width=${BIPARTITE.block}
                  height=${b.height}
                  rx="2"
                  fill=${fillFor(slot, b.row.fill)}
                  stroke=${`var(${slot})`}
                  @pointerenter=${(e) => {
                    this._hover = b.key;
                    this._showNode(e, model, b.row);
                  }}
                  @focus=${() => (this._hover = b.key)}
                  @blur=${() => (this._hover = null)}
                  @click=${() => this._pin(b.key)}
                  @dblclick=${() => this._openNode(b.row)}
                ></rect>
                <text
                  class="label"
                  x=${b.x + BIPARTITE.block + 8}
                  y=${b.top + b.height / 2}
                  dominant-baseline="middle"
                >${trimLabel(b.row.name, BIPARTITE.label)}</text>
              </g>`;
            },
          )}
        </g>
      </svg>
    `;
  }

  // -------------------------------------------------------------------------------------------
  // Chrome

  _controls(model) {
    const layout = this._layoutOf(model);
    const group = (options, current, onPick, label) => {
      const slug = label.toLowerCase();
      return html`
        <span class="segmented">
          <span>${label}</span>
          <span class="group" role="group" aria-label=${label}>
            ${options.map(
              ([key, text]) => html`
                <button
                  type="button"
                  data-control=${slug}
                  data-key=${key}
                  aria-pressed=${current === key ? 'true' : 'false'}
                  @click=${() => onPick(key)}
                >
                  ${text}
                </button>
              `,
            )}
          </span>
        </span>
      `;
    };
    return html`
      <div class="controls">
        ${group(Object.entries(LAYOUT_LABELS), layout, (k) => (this._layout = k), 'Draw')}
        ${group(
          CONTENTION_SPANS.map((s) => [s, SPAN_LABELS[s]]),
          this._span,
          (k) => this._setSpan(k),
          'Span',
        )}
        ${
          layout === 'chord'
            ? group(
                CONTENTION_VALUES.map((v) => [v, VALUE_LABELS[v]]),
                this._value,
                (k) => (this._value = k),
                'Width',
              )
            : nothing
        }
        ${
          layout === 'chord'
            ? group(
                CONTENTION_ORDERS.map((o) => [o, ORDER_LABELS[o]]),
                this._order,
                (k) => (this._order = k),
                'Order',
              )
            : nothing
        }
        <span class="segmented">
          <span>Show</span>
          <span class="group" role="group" aria-label="Kinds">
            ${CONTENTION_KINDS.map(
              (k) => html`
                <button
                  type="button"
                  data-control="kind"
                  data-key=${k}
                  aria-pressed=${this._kinds.includes(k) ? 'true' : 'false'}
                  @click=${() => this._toggleKind(k)}
                >
                  ${KIND_LABELS[k]}
                </button>
              `,
            )}
          </span>
        </span>
        <label class="field">
          Hide under
          <select @change=${(e) => (this._floor = Number(e.target.value))}>
            ${FLOORS.map(
              (f) =>
                html`<option value=${f} ?selected=${this._floor === f}>
                  ${f === 0 ? 'nothing' : `${f} h`}
                </option>`,
            )}
          </select>
        </label>
        <label class="field">
          Find
          <input
            type="search"
            placeholder="project or client"
            .value=${this._q}
            @input=${(e) => (this._q = e.target.value)}
          />
        </label>
        ${
          this._pinned
            ? html`<button class="link" type="button" @click=${() => (this._pinned = null)}>
                Clear the isolation
              </button>`
            : nothing
        }
      </div>
    `;
  }

  _kpis(summary) {
    return html`
      <div class="kpis">
        <div class="kpi">
          <div class="n">${exact(summary.thickest?.shared_hours ?? 0)}</div>
          <div class="l">thickest chord</div>
        </div>
        <div class="kpi">
          <div class="n">${count(summary.chords)}</div>
          <div class="l">competing pairs</div>
        </div>
        <div class="kpi">
          <div class="n">
            ${summary.shared_share === null ? '—' : percent(summary.shared_share)}
          </div>
          <div class="l">of the hours contended</div>
        </div>
        <div class="kpi">
          <div class="n">${count(summary.busiest?.drawn_entities ?? 0)}</div>
          <div class="l">most one person is on</div>
        </div>
      </div>
    `;
  }

  _legend(model) {
    const types = [];
    const seen = new Set();
    for (const node of model.ring.length ? model.ring : model.nodes) {
      const slot = node.slot;
      if (!slot || seen.has(slot.var)) continue;
      seen.add(slot.var);
      types.push({ var: slot.var, label: slot.label ?? node.canonical_type ?? 'Other' });
    }
    return html`
      <div class="legends">
        <ul class="legend">
          ${types
            .slice(0, 8)
            .map(
              (t) =>
                html`<li>
                  <span class="sw" style=${`background: var(${t.var})`}></span>${t.label}
                </li>`,
            )}
        </ul>
        <ul class="legend">
          ${CONFIDENCE_TIERS.map(
            (t) =>
              html`<li>
                <span
                  class="sw"
                  style=${`background: ${fillFor('--type-1', t.fill)}`}
                  aria-hidden="true"
                ></span>
                ${t.label} confidence${t.marker ? ` ${t.marker}` : ''}
              </li>`,
          )}
          <li>A chord wears the hue of its thicker end and the confidence of its shakier one.</li>
        </ul>
      </div>
    `;
  }

  _subtitle(model) {
    if (!model) return '';
    const span =
      model.span === 'week'
        ? `the week of ${longDate(model.week_start)}`
        : `${longDate(model.week_start)} to ${longDate(model.week_end)}`;
    const where = model.entity ? ` · ${model.entity.label}` : '';
    return `${span}${where} · ${plural(model.weeks_count, 'week')}`;
  }

  _notes(model, summary) {
    const layout = this._layoutOf(model);
    const notes = [];
    notes.push(
      html`<p class="note">
        A chord is what a pair <strong>cannot share</strong>: for every person on both, in every
        week, the smaller of their two allocations — added up over the people and the weeks. The
        minimum is taken inside the week on purpose, so two projects that never run together share
        nothing however large they both are. The bipartite view draws the hours themselves instead,
        which is why its bands are thicker than the ring's ribbons.
      </p>`,
    );

    if (model.span === 'week')
      notes.push(
        html`<p class="note">
          This is the week the assignment matrix above is drawn for, so a chord's people and their
          hours are the matrix's own cells — which is the check: pick the thickest chord, read the
          two columns, and the minimum of each row adds to the chord.
        </p>`,
      );
    else
      notes.push(
        html`<p class="note">
          Over a window a pair's hours are counted week by week and added, never netted and never
          taken as the minimum of two totals. Switch to <strong>Week</strong> to read a chord
          against the assignment matrix cell by cell.
        </p>`,
      );

    if (model.isolated.length)
      notes.push(
        html`<p class="note" data-note="isolated">
          ${plural(model.isolated.length, 'project')} share nobody and so have no chord and no arc —
          an arc is as long as the chords it carries, and nothing is not a length:
          ${model.isolated
            .slice(0, 6)
            .map((n) => n.name)
            .join(', ')}${model.isolated.length > 6 ? ', and others' : ''}.
          They are in the table with no competitors.
        </p>`,
      );

    if (summary.unassigned_hours > 0)
      notes.push(
        html`<p class="note" data-note="role-only">
          ${exact(summary.unassigned_hours)} of the demand drawn here is role-only — a service with
          nobody named for it — so it can compete for no one and appears in no chord. It is on the
          projects' own figures and in the table.
        </p>`,
      );

    if (summary.nonproject_hours > 0)
      notes.push(
        html`<p class="note" data-note="nonproject">
          The non-project baseline (${exact(summary.nonproject_hours)}) is left out altogether.
          Everybody carries it, so it would be joined to every project on the board and would be the
          thickest chord on every screen, which would say only that people have meetings.
        </p>`,
      );

    if (summary.withheld_count > 0)
      notes.push(
        html`<p class="note" data-note="withheld">
          ${plural(summary.withheld_count, 'person', 'people')} on this work are outside what you
          may see. Their hours are in the chords others already carry
          (${exact(summary.withheld_hours)}) and are never named, and a pair only they share is not
          drawn at all.
        </p>`,
      );

    if (summary.chords_dropped > 0)
      notes.push(
        html`<p class="note" data-note="dropped">
          The response carries the ${count(model.totals?.chords_shown ?? 0)} thickest of
          ${count(model.totals?.chords ?? 0)} pairs; ${count(summary.chords_dropped)} thinner ones
          are not drawn. Every project's own shared hours and competitor count are still counted
          over all of them.
        </p>`,
      );

    if (summary.nodes_hidden > 0 || summary.chords_hidden > 0)
      notes.push(
        html`<p class="note" data-note="hidden">
          The controls are hiding ${count(summary.nodes_hidden)} of the projects and
          ${count(summary.chords_hidden)} of the pairs. The arcs are as long as the chords still
          drawn, so hiding a pair shortens two arcs; the table and each project's own figures do not
          follow the controls.
        </p>`,
      );

    if (layout === 'bipartite')
      notes.push(
        html`<p class="note" data-note="bipartite">
          The bipartite alternate is what V-9 offers for a small team, and this card opens on it at
          or under ${count(BIPARTITE_MAX_PEOPLE)} people. A project's block is its
          <em>named</em> people's hours only: role-only and withheld hours have no band to be drawn
          in.
        </p>`,
      );

    return html`${notes}`;
  }

  // -------------------------------------------------------------------------------------------
  // Tables

  _table(name, caption, columns, rows) {
    return html`<data-table
      caption=${caption}
      export-name=${name}
      page-size="25"
      dense
      .columns=${columns}
      .rows=${rows}
    ></data-table>`;
  }

  get _hourFormats() {
    return {
      byUnit: {
        hours: (v) => (v === null || v === undefined ? '—' : exact(v)),
        percent: (v) => percent(v),
      },
    };
  }

  // -------------------------------------------------------------------------------------------
  // Render

  render() {
    const { run } = this.appState;
    const model = this._model;
    const summary = model ? contentionSummary(model) : null;

    let body;
    if (!run) body = html`<div class="state">No forecast run yet — nothing to draw.</div>`;
    else if (this._error) body = html`<div class="state error">${this._error}</div>`;
    else if (!this._data && this._loading)
      body = html`<div class="state">Reading who is on what…</div>`;
    else if (!model) body = html`<div class="state">No weeks in this window.</div>`;
    else if (!model.nodes.length)
      body = html`
        ${this._controls(model)}
        <div class="state" data-note="empty">
          No projects with hours in this span, so there is nothing to compete over.
        </div>
      `;
    else if (!model.chords.length)
      body = html`
        ${this._controls(model)}
        <div class="state" data-note="uncoupled">
          ${plural(model.nodes.length, 'project')} here and not one person on two of them: nothing
          is competing for anybody. ${this._notes(model, summary)}
        </div>
        ${this._tables(model)}
      `;
    else
      body = html`
        ${this._controls(model)}
        ${this._layoutOf(model) === 'bipartite' ? this._bipartite(model) : this._ring(model)}
        ${this._legend(model)} ${this._notes(model, summary)} ${this._tables(model)}
      `;

    return html`
      <div class="card" aria-busy=${this._loading ? 'true' : 'false'}>
        <div class="head">
          <div>
            <div class="eyebrow">Contention map</div>
            <h2>Which projects compete for the same people?</h2>
            <div class="sub">${this._subtitle(model)}</div>
          </div>
          ${summary ? this._kpis(summary) : nothing}
        </div>
        ${body}
        <chart-tooltip></chart-tooltip>
      </div>
    `;
  }

  _tables(model) {
    const sub = this._subtitle(model);
    return html`
      <details
        class="table"
        ?open=${this._tableOpen}
        @toggle=${(e) => (this._tableOpen = e.target.open)}
      >
        <summary>Table view</summary>
        ${this._table(
          'contention-chords',
          `The pairs — ${sub}`,
          withFormats(contentionChordColumns(), this._hourFormats),
          contentionChordRows(model),
        )}
        ${this._table(
          'contention-people',
          `Who is shared, and how the chord adds up — ${sub}`,
          withFormats(contentionContributorColumns(), this._hourFormats),
          contentionContributorRows(model),
        )}
        ${this._table(
          'contention-projects',
          `The projects — ${sub}`,
          withFormats(contentionNodeColumns(), this._hourFormats),
          contentionNodeRows(model),
        )}
        ${this._table(
          'contention-load',
          `How far each person is spread — ${sub}`,
          withFormats(contentionPeopleColumns(), this._hourFormats),
          contentionPeopleRows(model),
        )}
      </details>
    `;
  }
}

customElements.define('contention-map', ContentionMap);
